/* tslint:disable:object-literal-sort-keys */
import { IClientConfig, IDomainPageConfig, IPresentationConfig } from './clientConfig';
import { PageId } from './constants/pageId.enum';
import { SupportedClientCountryCode } from './constants/supportedClientCountryCode.enum';
import { TextHelpers } from '../utils/textHelpers';

export class ClientConfigFactory {
    public static makeClientConfig(clientConfigJSON: any): IClientConfig {
        if (
            !clientConfigJSON.clientId ||
            !clientConfigJSON.internalIdentifier ||
            !clientConfigJSON.serviceUuid ||
            !clientConfigJSON.lang ||
            !clientConfigJSON.returnUrl ||
            !clientConfigJSON.tokenExpirationUrl
        ) {
            throw new Error('Incorrectly defined client configuration');
        }

        const return_path = window.localStorage.getItem('RETURN_PATH') ?? '';
        const returnUrl = `${TextHelpers.templateRender(clientConfigJSON.returnUrl, {
            returnPath: return_path,
        })}`;
        const domainPages: { [key: string]: IDomainPageConfig } = {};

        if (clientConfigJSON.domainPages) {
            Object.keys(clientConfigJSON.domainPages).forEach((domain) => {
                if (clientConfigJSON.domainPages) {
                    domainPages[domain] = ClientConfigFactory.makeDomainPageConfig(
                        clientConfigJSON.domainPages[domain],
                    );
                }
            });
        }

        return {
            ...ClientConfigFactory.DEFAULT_CLIENT_CONFIG_TOP_LEVEL_VALUES,
            ...clientConfigJSON,
            returnUrl: returnUrl,
            presentationConfig: clientConfigJSON.presentationConfig
                ? ClientConfigFactory.makePresentationConfig(clientConfigJSON.presentationConfig)
                : ClientConfigFactory.DEFAULT_PRESENTATION_CONFIG,
            domainPages,
        } as IClientConfig;
    }

    private static makeDomainPageConfig(
        domainConfigJSON: Partial<IDomainPageConfig>,
    ): IDomainPageConfig {
        return {
            presentationConfig: domainConfigJSON.presentationConfig
                ? ClientConfigFactory.makePresentationConfig(domainConfigJSON.presentationConfig)
                : ClientConfigFactory.DEFAULT_PRESENTATION_CONFIG,
        } as IDomainPageConfig;
    }

    private static makePresentationConfig(
        presentationConfigJSON: Partial<IPresentationConfig>,
    ): IPresentationConfig {
        const defaultConfig = ClientConfigFactory.DEFAULT_PRESENTATION_CONFIG;
        const visibleFields = { ...defaultConfig.visibleFields };
        const showSections = { ...defaultConfig.showSections };

        if (presentationConfigJSON.visibleFields) {
            Object.keys(presentationConfigJSON.visibleFields).forEach((key: string) => {
                if (presentationConfigJSON.visibleFields && visibleFields[key]) {
                    visibleFields[key] = Object.assign(
                        {},
                        visibleFields[key],
                        presentationConfigJSON.visibleFields[key],
                    );
                }
            });
        }

        if (presentationConfigJSON.showSections) {
            Object.keys(presentationConfigJSON.showSections).forEach((key: string) => {
                if (presentationConfigJSON.showSections) {
                    showSections[key] = presentationConfigJSON.showSections[key];
                }
            });
        }

        // Use sideBar from JSON only if it's defined and not null, otherwise use default
        const sideBar =
            presentationConfigJSON.sideBar !== null && presentationConfigJSON.sideBar !== undefined
                ? presentationConfigJSON.sideBar
                : defaultConfig.sideBar;

        return {
            ...ClientConfigFactory.DEFAULT_PRESENTATION_CONFIG,
            ...presentationConfigJSON,
            sideBar,
            visibleFields,
            showSections,
        };
    }

    private static get DEFAULT_CLIENT_CONFIG_TOP_LEVEL_VALUES(): Partial<IClientConfig> {
        return {
            fullName: '',
            twoDigitCode: '',
            applyServiceHeader: false,
            countryCode: SupportedClientCountryCode.US,
            brandCodes: [
                'Visa',
                'MasterCard',
                'Diners Club',
                'Discover',
                'JCB',
                'American Express',
                'Maestro',
            ],
        };
    }

    private static get DEFAULT_PRESENTATION_CONFIG(): IPresentationConfig {
        return {
            header: {
                pointsUrl: '/points-club',
                showSignOut: false,
                userIDUrl: '/personal-information',
            },
            hideSideMenu: false,
            homePage: PageId.tiles,
            removeLinksFromHeader: false,
            showSections: {
                profile: false,
                security: false,
                services: false,
                points: false,
                cards: false,
                address: false,
            },
            sideBar: [
                'profile',
                'security',
                'cashbackAndPoints',
                'points',
                'services',
                'services.v2',
                'ucrm',
                'address',
                'cards',
                'address.jp',
                'ekyc',
                'cancel.membership',
                'linked.accounts',
            ],
            visibleFields: {
                profile: {
                    firstName: { name: 'firstName', required: true },
                    lastName: { name: 'lastName', required: true },
                },
                address: {},
                account: {
                    updateEmail: {
                        name: 'email',
                        required: true,
                    },
                    currentPassword: {
                        name: 'currentPassword',
                        required: true,
                    },
                    newPassword: {
                        name: 'newPassword',
                        required: true,
                    },
                    username: {
                        name: 'username',
                        required: true,
                    },
                },
                paymethod: {
                    cardNumber: {
                        name: 'cardNumber',
                        required: true,
                    },
                    cardHolder: {
                        name: 'cardHolder',
                        required: true,
                    },
                },
            },
        };
    }
}
